/** Pure OAuth 2.0 / PKCE helpers for the Guardius hosted-page sign-in flow.
 *  No React, no storage, no network — all unit-testable. SIWE itself happens
 *  on Guardius's /auth/authorize page; this module only builds the authorize
 *  and end-session URLs, mints the PKCE verifier/challenge + state, decodes JWT
 *  claims for client-side gating, and matches permission strings.
 *
 *  Adapted from penseur-trading/dashboard/src/lib/oauth.ts (server contract
 *  verified 2026-06-07 against ~/projects/experiments/penseur-guardius/server
 *  for param names and .../sdk/typescript/src/core/rbac-permissions.ts for
 *  permission matching). Watchtower tokens must be minted for the management
 *  tenant, so the console always supplies a concrete tenant_id (the Lambdas
 *  enforce `aud`); the invite/intent onboarding surface is not used here. */

/** The literal OAuth public-client id for this console. */
export const PLATFORM_ID = 'penseur-watchtower';

/** The RBAC permission the query API requires (usage read). Client-side gating
 *  only — the Lambdas' Guardius authorizer is the authoritative enforcement
 *  point.
 *
 *  THE `*` TENANT SEGMENT HERE IS DELIBERATE, AND IT NO LONGER MATCHES THE
 *  SERVER GATES. Every gate in `lib/lambdas/**` now reads
 *  `*:penseur-watchtower:{aud}:usage:*:…`, resolved from the token's verified
 *  `aud` (issue #134): the DELEGATED live check in Guardius uses
 *  `isPermissionCoveredBy`, which is ONE-DIRECTIONAL, so a required `*` tenant
 *  is not covered by a tenant-bound grant and every delegated request 403'd
 *  from 2026-10-12.
 *
 *  That trap cannot be reached from here, because this file never asks
 *  Guardius anything — `permissionMatches` below is the SYMMETRIC rule, run
 *  locally against the token's own `permissions[]`, and under it a `*` on
 *  either side matches. `oauth.test.ts` pins a concrete tenant-bound grant
 *  against this `*` required string for exactly that reason.
 *
 *  So the two spellings disagree ON PURPOSE. Do NOT "make them consistent":
 *  threading the resolved tenant through the console buys no behaviour change
 *  and would need a concrete `tenant_id` on every claims read, while copying
 *  this `*` back into a Lambda gate reintroduces the outage — which is what
 *  `test/permission-gates.test.ts` scans the Lambda sources to prevent. */
export const READ_PERMISSION = '*:penseur-watchtower:*:usage:*:read';

/** Typed subset of the Guardius access-token claims the console reads.
 *  No signature verification is done client-side — the service's Guardius
 *  authorizer is the enforcement point; these claims only drive UI gating. */
export interface Claims {
  sub: string;
  wallet_address?: string;
  /** The Guardius tenant UUID this token is scoped to. */
  tenant_id?: string;
  permissions?: string[];
  permissions_truncated?: boolean;
  exp?: number;
  /** Issued-at, seconds, on GUARDIUS's clock. Read only when a token arrives
   *  fresh, to learn the server clock offset (#289, `server-clock.ts`). */
  iat?: number;
}

/** base64url-encode raw bytes (RFC 4648 §5, no padding). */
function base64UrlEncode(bytes: Uint8Array): string {
  let bin = '';
  for (const b of bytes) bin += String.fromCharCode(b);
  return btoa(bin).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
}

/** base64url-decode to a UTF-8 string; throws on malformed input. */
function base64UrlDecode(input: string): string {
  const padded = input.replace(/-/g, '+').replace(/_/g, '/');
  // atob tolerates missing padding in most engines, but normalize for safety.
  const pad = padded.length % 4 === 0 ? '' : '='.repeat(4 - (padded.length % 4));
  const bin = atob(padded + pad);
  const bytes = new Uint8Array(bin.length);
  for (let i = 0; i < bin.length; i++) bytes[i] = bin.charCodeAt(i);
  return new TextDecoder().decode(bytes);
}

/** Generate a PKCE verifier + S256 challenge. The verifier is a 43-char
 *  base64url string (32 random bytes → no padding); the challenge is
 *  base64url(SHA-256(verifier)). Guardius enforces S256 + the 43-char
 *  base64url challenge shape. */
export async function generatePkce(): Promise<{ verifier: string; challenge: string }> {
  const verifierBytes = new Uint8Array(32);
  crypto.getRandomValues(verifierBytes);
  const verifier = base64UrlEncode(verifierBytes);
  const digest = await crypto.subtle.digest('SHA-256', new TextEncoder().encode(verifier));
  const challenge = base64UrlEncode(new Uint8Array(digest));
  return { verifier, challenge };
}

/** Opaque CSRF state: 32 random bytes → 43-char base64url. Guardius's
 *  /auth/authorize rejects a state shorter than 32 chars with WEAK_STATE, so
 *  16 bytes (22 chars) is too short — use 32. */
export function randomState(): string {
  const bytes = new Uint8Array(32);
  crypto.getRandomValues(bytes);
  return base64UrlEncode(bytes);
}

/** Decode the payload segment of a JWT into typed claims; `null` on anything
 *  malformed. NO signature verification client-side — UI gating only; the
 *  service's Guardius authorizer is the authoritative enforcement point. */
export function decodeJwtClaims(token: string): Claims | null {
  try {
    const parts = token.split('.');
    if (parts.length !== 3) return null;
    const json = base64UrlDecode(parts[1]);
    const claims = JSON.parse(json) as unknown;
    if (typeof claims !== 'object' || claims === null) return null;
    const c = claims as Record<string, unknown>;
    if (typeof c.sub !== 'string') return null;
    return c as unknown as Claims;
  } catch {
    return null;
  }
}

/** Segment-wise match over the 6-segment
 *  `region:platform:tenantId:resourceType:resourceId:action` permission format.
 *
 *  The Guardius server and SDK use SYMMETRIC wildcard matching — a `*` in
 *  EITHER the granted segment OR the required segment matches that position.
 *  This is NOT the naive "granted `*` matches anything" rule; the required
 *  string also gets wildcard treatment. It matters here because the console
 *  builds a required string with a `*` region segment
 *  (`*:penseur-watchtower:*:usage:*:read`). Both sides also return false for
 *  any string that isn't exactly 6 segments (never throw). This implementation
 *  matches that semantics exactly, so this gating agrees with the NON-delegated
 *  server path, which decides offline from the same `permissions[]` with the
 *  same symmetric rule. It is NOT the rule the delegated live check uses — see
 *  READ_PERMISSION above and issue #134. */
export function permissionMatches(granted: string[] | undefined, required: string): boolean {
  if (!granted) return false;
  const c = required.split(':');
  if (c.length !== 6) return false;
  return granted.some((grant) => {
    const g = grant.split(':');
    if (g.length !== 6) return false;
    for (let i = 0; i < 6; i++) {
      if (g[i] !== '*' && c[i] !== '*' && g[i] !== c[i]) return false;
    }
    return true;
  });
}

/** Build the Guardius hosted-page authorize URL.
 *
 *  `platform_id` is REQUIRED (the literal console client id). `tenant_id` is
 *  set only when `config.tenantId` is non-empty: Watchtower tokens must be
 *  minted for the management tenant, so the console supplies it; an empty
 *  tenantId omits the param (the Lambdas enforce `aud`). */
export function buildAuthorizeUrl(
  config: { guardiusUrl: string; tenantId: string },
  args: {
    challenge: string;
    state: string;
    redirectUri: string;
  },
): string {
  const url = new URL('/auth/authorize', config.guardiusUrl);
  const params = new URLSearchParams({
    response_type: 'code',
    redirect_uri: args.redirectUri,
    code_challenge: args.challenge,
    code_challenge_method: 'S256',
    state: args.state,
    platform_id: PLATFORM_ID,
  });
  if (config.tenantId) params.set('tenant_id', config.tenantId);
  url.search = params.toString();
  return url.toString();
}

/** Build the Guardius OIDC RP-Initiated Logout URL
 *  (`GET /auth/end-session`, `docs/api-reference.md` §"1. Authentication
 *  (SIWE)" → "GET /auth/end-session", advertised as `end_session_endpoint` in
 *  the discovery document).
 *
 *  THIS URL IS REACHED BY A TOP-LEVEL BROWSER NAVIGATION, NEVER AN XHR — and
 *  the REASON is not the one everybody writes. A cross-origin `fetch` sends no
 *  cookies at all by default (`credentials` defaults to `same-origin`), and
 *  asking for them does not help: `OPTIONS /auth/logout` with our `Origin`
 *  returns `allow-origin`, `-methods`, `-headers`, `-expose-headers` and
 *  `-max-age` but NO `Access-Control-Allow-Credentials` (verified by curl
 *  2026-10-25, zero occurrences), so the browser refuses to attach the cookie
 *  even under `credentials: 'include'`. The API-only route is refused by the
 *  PREFLIGHT, not by the cookie's attributes. `SameSite=Lax` is NOT the
 *  blocker — `watchtower.penseur.ai` and `guardius.penseur.ai` share one
 *  registrable domain (`penseur.ai`), so they are same-SITE and merely
 *  cross-ORIGIN, and Lax PERMITS the cookie; Guardius's own docs say as much.
 *  Nor is `__Host-`: it scopes the cookie to `guardius.penseur.ai`, which is
 *  exactly where the request is going. We are deliberately NOT asking for
 *  credentialed CORS — on an auth endpoint it widens CSRF surface across every
 *  route sharing that configuration, to save one navigation. Full three-part
 *  chain and the ruling: `AGENTS.md`, "You can't just call the API and sign
 *  them out?" (issue #200).
 *
 *  Parameters, and why both authority params are sent:
 *  - **In Guardius a `client_id` IS a tenant id.** When present it is the
 *    authority, must name an active tenant, and `post_logout_redirect_uri` is
 *    validated against THAT tenant's registered origins.
 *  - `platform_id` is the authority only when neither `client_id` nor
 *    `id_token_hint` is given, and an empty `client_id=` is treated as absent —
 *    which is why both go on the query string: local dev runs with
 *    `tenantId: ''`, and `platform_id` is what still resolves an authority
 *    there. So `client_id` is set only when `config.tenantId` is non-empty.
 *  - `post_logout_redirect_uri` is checked by `validateRedirectUri`, the SAME
 *    validator the authorize flow uses, so the already-registered sign-in URI
 *    serves and no Guardius mutation is needed.
 *  - No `state`: nothing consumes it on return. No `id_token_hint`: the Console
 *    never receives an id_token. */
export function buildEndSessionUrl(
  config: { guardiusUrl: string; tenantId: string },
  args: { postLogoutRedirectUri: string },
): string {
  const url = new URL('/auth/end-session', config.guardiusUrl);
  const params = new URLSearchParams({
    platform_id: PLATFORM_ID,
    post_logout_redirect_uri: args.postLogoutRedirectUri,
  });
  if (config.tenantId) params.set('client_id', config.tenantId);
  url.search = params.toString();
  return url.toString();
}
